// Phase 5 (web revival) — Stream D.
//
// Right-hand control rail. Owns NO state of its own — derives every
// control's value from the parent's `ops` array and reports back via
// `onOpsChange`. The single source of truth for the in-flight edit
// lives in Edit.tsx so the preview-debouncer, save mutations, and
// hydrate-from-server flows all observe the same array.
//
// Op upsert/remove helpers below keep the array shape tidy: at most one
// op of each kind, and color sliders near zero get removed entirely so
// the wire payload doesn't carry no-op rows.

import type { EditOp, FilterName } from '../../api/types';
import { RotateControl } from './RotateControl';
import { CropOverlay, type CropValue } from './CropOverlay';
import {
  ColorSliders,
  COLOR_OP_NAMES,
  type ColorOpName,
} from './ColorSliders';
import { FilterPicker } from './FilterPicker';

interface OpsPanelProps {
  ops: EditOp[];
  onOpsChange: (ops: EditOp[]) => void;
}

type Deg = 0 | 90 | 180 | 270;

function readRotate(ops: EditOp[]): Deg {
  const op = ops.find((o) => o.op === 'rotate');
  return op ? op.deg : 0;
}

function readCrop(ops: EditOp[]): CropValue | null {
  const op = ops.find((o) => o.op === 'crop');
  return op ? { x: op.x, y: op.y, w: op.w, h: op.h } : null;
}

function readFilter(ops: EditOp[]): FilterName | null {
  const op = ops.find((o) => o.op === 'filter');
  return op ? op.name : null;
}

function readColors(ops: EditOp[]): Record<ColorOpName, number> {
  const out: Record<ColorOpName, number> = {
    brightness: 0,
    contrast: 0,
    saturation: 0,
    exposure: 0,
    shadows: 0,
    highlights: 0,
    vignette: 0,
  };
  for (const op of ops) {
    if (COLOR_OP_NAMES.includes(op.op as ColorOpName)) {
      out[op.op as ColorOpName] = (op as { v: number }).v;
    }
  }
  return out;
}

function withoutOp(ops: EditOp[], kind: EditOp['op']): EditOp[] {
  return ops.filter((o) => o.op !== kind);
}

function upsertOp(ops: EditOp[], next: EditOp): EditOp[] {
  return [...withoutOp(ops, next.op), next];
}

export function OpsPanel({ ops, onOpsChange }: OpsPanelProps) {
  const rotate = readRotate(ops);
  const crop = readCrop(ops);
  const colors = readColors(ops);
  const filter = readFilter(ops);

  function setRotate(deg: Deg) {
    if (deg === 0) {
      onOpsChange(withoutOp(ops, 'rotate'));
    } else {
      onOpsChange(upsertOp(ops, { op: 'rotate', deg }));
    }
  }

  function setCrop(next: CropValue | null) {
    if (next === null) {
      onOpsChange(withoutOp(ops, 'crop'));
      return;
    }
    onOpsChange(
      upsertOp(ops, { op: 'crop', x: next.x, y: next.y, w: next.w, h: next.h }),
    );
  }

  function setColor(name: ColorOpName, v: number) {
    if (Math.abs(v) <= 0.01) {
      onOpsChange(withoutOp(ops, name));
      return;
    }
    onOpsChange(upsertOp(ops, { op: name, v } as EditOp));
  }

  function setFilter(name: FilterName | null) {
    if (name === null) {
      onOpsChange(withoutOp(ops, 'filter'));
      return;
    }
    onOpsChange(upsertOp(ops, { op: 'filter', name }));
  }

  return (
    <aside className="flex h-full w-72 flex-col overflow-y-auto border-l border-neutral-800 bg-neutral-900 px-4 py-4">
      <div className="space-y-5">
        <RotateControl value={rotate} onChange={setRotate} />
        <div className="border-t border-neutral-800" />
        <CropOverlay value={crop} onChange={setCrop} />
        <div className="border-t border-neutral-800" />
        <ColorSliders values={colors} onChange={setColor} />
        <div className="border-t border-neutral-800" />
        <FilterPicker value={filter} onChange={setFilter} />
      </div>
    </aside>
  );
}
